import { createSlice } from "@reduxjs/toolkit";
import {
	type AuthError,
	multiFactor,
	signOut,
} from "firebase/auth";
import { jwtDecode } from "jwt-decode";
import { toast } from "react-toastify";

import { auth } from "../../firebase/firebaseConfig";
import { type TUserAuthState } from "../../types/user.type";
import {
	clearActiveTenant,
	getActiveTenant,
	syncActiveTenantFromAuthClaims,
} from "../../utils/tenantUtils";

import {
	getAllUsersInTenantThunk,
	passwordUpdatedThunk,
	userLoginThunk,
} from "./user.thunk";

// This file defines the Redux slice for user authentication.
// It includes the initial state, action creators, and reducers.
// The slice handles various logics related to user authentication.

// Define the initial state for user authentication
const initialState: TUserAuthState = {
	token: null,
	decodedToken: null,
	usernameFromToken: null,
	loginError: null,
	authStatus: {
		isLoggedIn: false,
		isLoading: true,
	},
	isPasswordSet: false,
	isMultiFactorAuthSet: false,
	loading: false,
	user: null,
	mfaError: null,
	isGlobalAdmin: null,
	allUsers: {
		data: [],
		loading: false,
		error: null,
	},
};

// Create the userAuthSlice using createSlice function from Redux Toolkit
export const userAuthSlice = createSlice({
	name: "auth",
	initialState,
	reducers: {
		// Reducer to set the authentication token
		setAuthenticated: (state, action) => {
			state.token = action.payload;
			const decoded: any = jwtDecode(
				action.payload as string
			);
			state.decodedToken = decoded;
			state.usernameFromToken = decoded.name;
			state.isPasswordSet = decoded.email_verified;
			syncActiveTenantFromAuthClaims(decoded);
		},
		setLoginError: (state, action) => {
			state.loginError = action.payload;
		},
		// Reducer to set the loading state of authentication
		setAuthLoading: (state, action) => {
			state.authStatus.isLoading = action.payload;
		},
		setMfaError: (
			state,
			action: { payload: AuthError | null }
		) => {
			state.mfaError = action.payload;
			const user = auth.currentUser;
			if (user) {
				state.isMultiFactorAuthSet =
					multiFactor(user).enrolledFactors.length > 0;
			} else {
				// If we have an error, it means MFA is required/active
				state.isMultiFactorAuthSet = !!action.payload;
			}
		},
		// Reducer to handle user logout
		logout: (state) => {
			state.token = null;
			state.decodedToken = null;
			state.usernameFromToken = null;
			state.loginError = null;
			state.authStatus.isLoggedIn = false;
			state.authStatus.isLoading = false;
			state.isPasswordSet = false;
			state.isMultiFactorAuthSet = false;
			state.loading = false;
			state.user = null;
			state.mfaError = null;
			state.isGlobalAdmin = null;
			localStorage.clear();
			clearActiveTenant();
		},
		updateGlobalAdmin: (state, action) => {
			const activeTenant = action.payload;
			if (state.user?.tenants) {
				const allTenants = state.user?.tenants;
				const tenant = allTenants[activeTenant];
				if (
					state.user.global_role === "global_admin" ||
					state.user.global_role === "global_tenant_admin"
				) {
					state.isGlobalAdmin = true;
				} else {
					if (tenant?.role === "tenant_admin") {
						state.isGlobalAdmin = true;
					} else {
						state.isGlobalAdmin = false;
					}
				}
			}
		},
	},
	extraReducers: (builder) => {
		// Extra reducers to handle async actions for user login
		builder
			.addCase(userLoginThunk.pending, (state) => {
				state.authStatus.isLoading = true;
				state.loginError = null;
			})
			.addCase(
				userLoginThunk.fulfilled,
				(state, action) => {
					const meta = action.payload;
					state.loginError = null;
					state.user = meta;
					const activeTenant = getActiveTenant();
					if (
						meta.global_role === "global_admin" ||
						meta.global_role === "global_tenant_admin"
					) {
						state.isGlobalAdmin = true;
					} else if (meta.global_role === "scoped_user") {
						if (activeTenant) {
							const tenant = meta.tenants[activeTenant];
							if (tenant?.role === "tenant_admin") {
								state.isGlobalAdmin = true;
							} else {
								state.isGlobalAdmin = false;
							}
						} else {
							const firstTenant = Object.keys(
								meta.tenants
							)[0];
							const tenant = meta.tenants[firstTenant];
							if (tenant.role === "tenant_admin") {
								state.isGlobalAdmin = true;
							} else {
								state.isGlobalAdmin = false;
							}
						}
					} else {
						state.isGlobalAdmin = false;
					}
					state.authStatus.isLoggedIn = true;
					state.isPasswordSet = meta.isPasswordSet;
					state.authStatus.isLoading = false;
				}
			)
			.addCase(userLoginThunk.rejected, (state, action) => {
				state.token = null;
				state.decodedToken = null;
				state.usernameFromToken = null;
				state.user = null;
				state.isPasswordSet = false;
				state.isMultiFactorAuthSet = false;
				state.isGlobalAdmin = null;
				state.mfaError = null;
				state.authStatus.isLoggedIn = false;
				state.authStatus.isLoading = false;
				state.loginError =
					typeof action.payload === "string"
						? action.payload
						: action.error.message ??
							"We couldn't complete sign in. Please try again.";
				void signOut(auth).catch(() => undefined);
			});

		// Extra reducers to handle async actions for password update
		builder
			.addCase(passwordUpdatedThunk.pending, (state) => {
				state.loading = true;
			})
			.addCase(
				passwordUpdatedThunk.fulfilled,
				(state, action) => {
					if (action.payload.success) {
						// state.isPasswordSet = true;
						toast.success("Password updated successfully");
						void signOut(auth).then(() => {
							window.location.reload();
						});
					} else {
						state.isPasswordSet = false;
						toast.error("Password update failed");
					}
					state.loading = false;
					// state.isPasswordSet = true;
				}
			)
			.addCase(passwordUpdatedThunk.rejected, (state) => {
				state.loading = false;
			});

		builder
			.addCase(
				getAllUsersInTenantThunk.pending,
				(state) => {
					state.allUsers.loading = true;
				}
			)
			.addCase(
				getAllUsersInTenantThunk.fulfilled,
				(state, action) => {
					state.allUsers.loading = false;
					const temp = Object.values(action.payload);
					state.allUsers.data = temp;
				}
			)
			.addCase(
				getAllUsersInTenantThunk.rejected,
				(state, action) => {
					state.allUsers.loading = false;
					state.allUsers.error = action.error
						? action.error.message
						: null;
				}
			);
	},
});

// Export the action creators and reducer from the userAuthSlice
export const UserAuthActions = userAuthSlice.actions;
export const UserAuthReducer = userAuthSlice.reducer;
